import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { demoDataGenerator } from '../../utils/demoDataGenerator';

const PreviewTeamPerformance = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('weekly');
  
  const demoData = useMemo(() => demoDataGenerator?.getTeamPerformance(), []);
  const { metrics, teamMembers, tasks } = demoData;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Preview Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Icon name="Eye" size={24} />
              <div>
                <h3 className="font-semibold text-lg">Preview Mode - Team Performance Hub</h3>
                <p className="text-sm text-purple-100">Sign up to manage your team and track real performance metrics</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="bg-white text-purple-600 hover:bg-purple-50 border-white"
                onClick={() => navigate('/pricing-page')}
              >
                View Plans
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => navigate('/login')}
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Team Performance Hub</h1>
          <p className="text-gray-600 mt-2">Monitor team efficiency, task completion, and workload distribution</p>
        </div>

        {/* Period Selector */}
        <div className="mb-6 flex gap-2">
          {['daily', 'weekly', 'monthly', 'quarterly']?.map(period => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
                selectedPeriod === period
                  ? 'bg-blue-600 text-white' :'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              {period}
            </button>
          ))}
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {metrics?.map((metric, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">{metric?.title}</span>
                <Icon name={metric?.icon} size={20} className={`text-${metric?.color}-600`} />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {metric?.value}{metric?.unit}
              </div>
              <div className={`text-sm mt-1 ${
                metric?.change > 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {metric?.change > 0 ? '↑' : '↓'} {Math.abs(metric?.change)}% vs target
              </div>
              <div className="mt-3 bg-gray-200 rounded-full h-2">
                <div
                  className={`bg-${metric?.color}-600 h-2 rounded-full`}
                  style={{ width: `${(metric?.value / metric?.target) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Team Leaderboard */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Team Leaderboard</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {teamMembers?.map((member, index) => (
                <div key={member?.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white font-bold">
                      #{index + 1}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{member?.name}</div>
                      <div className="text-sm text-gray-500">{member?.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{member?.tasksCompleted}</div>
                      <div className="text-xs text-gray-500">Tasks</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{member?.efficiency}%</div>
                      <div className="text-xs text-gray-500">Efficiency</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Active Tasks */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Active Tasks</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {tasks?.map(task => (
              <div key={task?.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{task?.title}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task?.status)}`}>
                        {task?.status?.replace('_', ' ')}
                      </span>
                      <Icon name="Flag" size={16} className={getPriorityColor(task?.priority)} />
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Icon name="User" size={14} />
                        <span>{task?.assignee}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        <span>Due: {task?.dueDate}</span>
                      </div>
                      {task?.completedDate && (
                        <div className="flex items-center gap-1 text-green-600">
                          <Icon name="CheckCircle" size={14} />
                          <span>Completed: {task?.completedDate}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-8 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-8 border-2 border-purple-200">
          <div className="text-center max-w-2xl mx-auto">
            <Icon name="Users" size={48} className="text-purple-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Unlock Team Management Features</h3>
            <p className="text-gray-600 mb-6">
              Upgrade to Commercial Operation plan to manage unlimited team members, track performance metrics, and optimize workload distribution across your facility.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                onClick={() => navigate('/pricing-page')}
              >
                Start 14-Day Free Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-purple-600 text-purple-600 hover:bg-purple-50"
                onClick={() => navigate('/login')}
              >
                Sign Up Free
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">✓ Unlimited team members • ✓ Real-time tracking • ✓ Performance analytics</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewTeamPerformance;